import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface TrailDescription {
  id: string;
  trail_id: string;
  description: string;
  resource_links: any;
  source: string;
  status: string;
  created_at: string;
  updated_at: string;
  submitted_by: string | null;
  reviewed_by: string | null;
  moderation_notes: string | null;
  superseded_by: string | null;
  regeneration_context: any;
  regeneration_count: number;
}

export const useTrailDescription = (trailId: string | undefined) => {
  const [description, setDescription] = useState<TrailDescription | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();

  const fetchDescription = async () => {
    if (!trailId) {
      setDescription(null);
      return;
    }
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("trail_descriptions")
        .select("*")
        .eq("trail_id", trailId)
        .eq("status", "active")
        .single();

      if (!error && data) {
        setDescription(data as TrailDescription);
      } else {
        setDescription(null);
      }
    } catch (err) {
      console.error("Error fetching trail description:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setDescription(null); // Clear previous description when trail changes
    fetchDescription();
  }, [trailId]);

  const generateDescription = async () => {
    if (!trailId || isGenerating) return;

    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "generate-trail-description",
        {
          body: { trailId },
        }
      );

      if (error) throw error;

      if (data?.description) {
        setDescription(data.description);
        toast({
          title: data.cached ? "Trail info loaded" : "Research complete!",
          description: data.cached
            ? "Using previously researched trail information"
            : "We found some information about this trail",
        });
      }
    } catch (err: any) {
      console.error("Error generating description:", err);
      toast({
        title: "Failed to generate description",
        description: err.message || "Please try again later",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Refresh description (useful after regeneration)
  const refreshDescription = () => {
    fetchDescription();
  };

  // Check if description is flagged
  const isFlagged = description?.status === "flagged_inaccurate";

  return {
    description,
    isLoading,
    isGenerating,
    isFlagged,
    generateDescription,
    refreshDescription,
  };
};
